import { cn } from "./classNames-BzrkDnOD.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { AlertCircle, AlertTriangle, CircleCheck, Info, Loader2, X } from "./icons-DcNsvqse.js";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
import { toast } from "sonner";
//#region src/components/Toast/ToastAction.tsx
const ToastAction = ({ ref, className, ...rest }) => /* @__PURE__ */ jsx("button", {
	...rest,
	ref,
	"data-slot": "toast-action",
	type: "button",
	className: cn("tw:col-start-3", "tw:row-start-1", "tw:flex", "tw:items-center", "tw:justify-center", "tw:text-xs", "tw:font-medium", "tw:text-primary", "tw:px-2", "tw:h-6", "tw:bg-white", "tw:rounded", className)
});
//#endregion
//#region src/components/Toast/ToastDescription.tsx
const ToastDescription = ({ ref, className, children, ...rest }) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-slot": "toast-description",
	className: cn("tw:col-start-2", "tw:font-normal", "tw:break-words", "tw:text-white", "tw:group-data-[variant=default]/toast:text-muted-foreground", className),
	...rest,
	children
});
//#endregion
//#region src/components/Toast/ToastDismiss.tsx
const ToastDismiss = ({ ref, className, ...rest }) => /* @__PURE__ */ jsxs("button", {
	ref,
	"data-slot": "toast-dismiss",
	className: cn("tw:flex", "tw:justify-center", "tw:col-start-4", "tw:row-start-1", "tailwindcss-preflight", "tw:[&_svg]:aspect-square", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", "tw:[&_svg]:m-1", "tw:text-white", "tw:group-data-[variant=default]/toast:[&_svg]:text-popover-foreground", "tw:group-data-[variant=default]/toast:group-data-[argo=true]/toast:[&_svg]:text-foreground", className),
	type: "button",
	...rest,
	children: [/* @__PURE__ */ jsx(X, {}), /* @__PURE__ */ jsx("span", {
		className: "tw:sr-only",
		children: "Dismiss"
	})]
});
//#endregion
//#region src/components/Toast/ToastDuration.tsx
const ToastDuration = ({ ref, duration, className, style, ...rest }) => /* @__PURE__ */ jsx("span", {
	...rest,
	ref,
	className: cn("tw:bg-white/30", "tw:group-data-[variant=default]/toast:bg-popover-foreground/20", "tw:group-data-[variant=default]/toast:group-data-[argo=true]/toast:bg-foreground/20", "tw:rounded-bl-lg", "tw:mx-px", "tw:absolute", "tw:bottom-0", "tw:left-0", "tw:h-1", "tw:animate-toast-progress", "tw:group-hover/toast:[animation-play-state:paused]", className),
	style: {
		animationDuration: `${duration}ms`,
		...style
	}
});
//#endregion
//#region src/components/Toast/ToastTitle.tsx
const ToastTitle = ({ ref, className, children, ...rest }) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-slot": "toast-title",
	className: cn("tw:col-start-2", "tw:text-sm", "tw:leading-6", "tw:min-h-6", "tw:self-center", "tw:text-white", "tw:group-data-[variant=default]/toast:text-popover-foreground", "tw:group-data-[variant=default]/toast:group-data-[argo=true]/toast:text-foreground", "tw:break-words", "tw:font-medium", className),
	...rest,
	children
});
//#endregion
//#region src/components/Toast/ToastRoot.tsx
const variants = cva([
	"tailwindcss-preflight",
	"tw:group/toast",
	"tw:relative",
	"tw:grid",
	"tw:grid-cols-[0_1fr_auto_auto]",
	"tw:items-start",
	"tw:has-[[data-slot=toast-description]]:grid-rows-[auto_auto]",
	"tw:has-[[data-slot=toast-icon]]:grid-cols-[auto_1fr_auto_auto]",
	"tw:[&_[data-slot=toast-icon]]:mr-2",
	"tw:[&_[data-slot=toast-title]]:mr-2.5",
	"tw:[&_[data-slot=toast-description]]:mr-2.5",
	"tw:[&_[data-slot=toast-action]]:mr-2",
	"tw:[&_[data-slot=toast-description]]:col-end-3",
	"tw:gap-y-0",
	"tw:p-4",
	"tw:rounded-lg",
	"tw:shadow-md"
], {
	defaultVariants: { variant: "info" },
	variants: { variant: {
		default: "tw:border tw:border-border tw:bg-popover",
		error: "tw:bg-error-550",
		info: "tw:bg-blue-600",
		success: "tw:bg-green-700",
		warning: "tw:bg-warning-550"
	} }
});
const DEFAULT_VARIANT_ICON = {
	error: /* @__PURE__ */ jsx(AlertTriangle, {}),
	info: /* @__PURE__ */ jsx(Info, {}),
	success: /* @__PURE__ */ jsx(CircleCheck, {}),
	warning: /* @__PURE__ */ jsx(AlertCircle, {})
};
const ToastRoot = ({ id, title, description, action, duration = Infinity, variant = "info", loading = false, icon: iconProp }) => {
	const renderArgo = useArgoDesign();
	const icon = (() => {
		if (loading) return /* @__PURE__ */ jsx(Loader2, { className: "tw:animate-spin" });
		if (iconProp) return iconProp;
		if (variant === "default" || variant === null) return null;
		return DEFAULT_VARIANT_ICON[variant];
	})();
	const handleActionClick = (event) => {
		toast.dismiss(id);
		action?.onClick(event);
	};
	return /* @__PURE__ */ jsxs("div", {
		"data-variant": variant,
		"data-argo": renderArgo,
		className: cn(variants({ variant }), "tw:data-[variant=default]:data-[argo=true]:bg-background"),
		children: [
			icon && /* @__PURE__ */ jsx("span", {
				"data-slot": "toast-icon",
				className: cn("tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:self-center", "tw:aspect-square", "tw:size-5", "tw:shrink-0", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5", "tw:text-white", "tw:group-data-[variant=default]/toast:text-popover-foreground", "tw:group-data-[variant=default]/toast:group-data-[argo=true]/toast:text-foreground"),
				children: icon
			}),
			/* @__PURE__ */ jsx(ToastTitle, { children: title }),
			description && /* @__PURE__ */ jsx(ToastDescription, { children: description }),
			action && /* @__PURE__ */ jsx(ToastAction, {
				style: action?.actionButtonStyle,
				onClick: handleActionClick,
				children: action.label
			}),
			/* @__PURE__ */ jsx(ToastDismiss, { onClick: () => toast.dismiss(id) }),
			duration !== Infinity && /* @__PURE__ */ jsx(ToastDuration, { duration })
		]
	});
};
//#endregion
//#region src/components/Toast/toast.tsx
const toast$1 = ({ onAutoClose, onDismiss, testId, toasterId, dismissible = true, position, title: titleProp, ...toast$2 }) => {
	const duration = (() => {
		if (typeof toast$2.duration === "number") return toast$2.duration;
		if (toast$2.variant === "error" || toast$2.variant === "warning") return Infinity;
		return toast$2.duration ?? 5e3;
	})();
	const renderCustomToast = (id) => {
		const title = typeof titleProp === "function" ? titleProp({ dismiss: () => toast.dismiss(id) }) : titleProp;
		return /* @__PURE__ */ jsx(ToastRoot, {
			...toast$2,
			id,
			title,
			duration
		});
	};
	return toast.custom(renderCustomToast, {
		...toast$2.id != null ? { id: toast$2.id } : {},
		unstyled: true,
		position,
		onAutoClose,
		onDismiss,
		testId,
		toasterId,
		dismissible,
		duration,
		className: cn("tw:rounded-lg", "tw:focus-visible:!ring-4", "tw:focus-visible:!ring-ring/50")
	});
};
toast$1.success = (title, options) => toast$1({
	...options,
	title,
	variant: "success"
});
toast$1.error = (title, options) => toast$1({
	...options,
	title,
	variant: "error",
	duration: options?.duration ?? Infinity
});
toast$1.warning = (title, options) => toast$1({
	...options,
	title,
	variant: "warning",
	duration: options?.duration ?? Infinity
});
toast$1.info = (title, options) => toast$1({
	...options,
	title,
	variant: "info"
});
toast$1.dismiss = (toastId) => toast.dismiss(toastId);
toast$1.dismissAll = () => toast.dismiss();
//#endregion
export { toast$1 as toast };

//# sourceMappingURL=toast-C_9FC637.js.map